import { useEffect } from 'react';
import { NavLink, Outlet, useParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';

import { BackText, Loading, Panel, PanelContent, PanelHeader } from '../atoms';
import { withAuthRedirect } from '../hocs';
import { AccountMenu } from '../molecules';
import {
  getMerchantDetails,
  getProductQueries,
  queryAvailableTypes,
  useAccountStore,
  useProductStore
} from '../../contexts';

type TAccountPage = {
  currentPage?: string;
};

const AccountPage = ({ currentPage }: TAccountPage) => {
  const { merchantId = '' } = useParams();

  const clearState = useAccountStore((s) => s.clearState);
  const setMerchantDetails = useAccountStore((s) => s.setMerchantDetails);
  const merchantDetails = useAccountStore((s) => s.merchantDetails);
  const currentProductIds = useAccountStore((s) => s.currentProductIds);
  const addCurrentProduct = useAccountStore((s) => s.addCurrentProduct);
  const setProductsLoading = useAccountStore(
    (s) => s.setCurrentProductsLoading
  );

  const setAvailableTypes = useProductStore((s) => s.setAvailableTypes);
  const setAvailableTypesLoading = useProductStore(
    (s) => s.setAvailableTypesLoading
  );

  const { isLoading: isMerchantLoading } = getMerchantDetails({
    setMerchantDetails,
    merchantId,
    query: useQuery
  });

  const productQueries = getProductQueries({
    queries: useQueries,
    currentProductIds,
    addCurrentProduct,
    merchantId
  });

  useEffect(() => {
    // Clear the shared local state when we navigate away from the page
    return () => clearState();
  }, [clearState]);

  queryAvailableTypes({
    setAvailableTypes,
    setAvailableTypesLoading,
    query: useQuery,
    merchantId
  });

  useEffect(() => {
    setProductsLoading(productQueries.some((p) => p.isLoading));
  }, [productQueries, setProductsLoading]);

  if (!merchantId) return null;

  return (
    <Panel>
      <PanelHeader>
        <span className="h3">Account</span>
        {merchantDetails && <h1>{merchantDetails.accountName}</h1>}
        <div>
          <NavLink to={`/accounts`}>
            <BackText text="Back to accounts" />
          </NavLink>
        </div>
      </PanelHeader>
      <PanelContent>
        {isMerchantLoading && <Loading />}
        {merchantDetails && (
          <>
            <AccountMenu merchantId={merchantId} />
            <Outlet />
          </>
        )}
      </PanelContent>
    </Panel>
  );
};

const SecureAccountPage = withAuthRedirect(AccountPage);
export { SecureAccountPage as AccountPage };
